export type ScaleTable = Array<[threshold: number, a: number, b: number]>;

/**
 * Scale 2: With Tax-Free Threshold (from ATO NAT 1007 1 July 2026)
 */
export const S2: ScaleTable = [
  [0, 0, 0],
  [362, 0.15, 54.3462],
  [538, 0.25, 108.2135],
  [673, 0.17, 54.3473],
  [721, 0.179, 60.8377],
  [865, 0.3227, 185.1935],
  [1282, 0.32, 181.7319],
  [2596, 0.39, 363.4627],
  [3653, 0.47, 655.7704],
];

/**
 * Scale 1: No Tax-Free Threshold (from ATO NAT 1007 1 July 2026)
 */
export const S1: ScaleTable = [
  [0, 0.15, 0.15],
  [188, 0.2084, 11.0185],
  [371, 0.179, 0.1066],
  [515, 0.3227, 74.1674],
  [932, 0.32, 71.6508],
  [2246, 0.39, 228.8816],
  [3303, 0.47, 493.1893],
];

export interface BestMatch {
  T: number;
  d: number;
}

export interface SolveResult {
  hits: number[];
  best: BestMatch;
}

export interface CalculationCardResult {
  title: string;
  gross: number;
  taxWithheld: number;
  net: number;
  superAmount: number;
  grossPlusSuper: number;
}

/**
 * Forward calculation: calculates monthly PAYG tax withheld (in whole dollars) from gross pay in cents.
 * @param c Gross pay in cents (integer)
 * @param S Scale table (S2 = with TFT, S1 = no TFT)
 */
export function calculateTaxFromCents(c: number, S: ScaleTable): number {
  const g = c / 100;
  if (g > 12675) {
    if (S === S2) {
      return Math.round(g < 15829.67 ? 3371 + 0.39 * (g - 12675) : 4602 + 0.47 * (g - 15829.66));
    }
    return Math.round(g < 14313 ? 3952 + 0.39 * (g - 12675) : 4593 + 0.47 * (g - 14312.99));
  }
  const x = Math.floor((3 / 13) * (g + (c % 100 === 33 ? 0.01 : 0)) + 1e-9);
  let r = S[0];
  for (const s of S) {
    if (s[0] <= x) r = s;
  }
  const w = Math.round((x + 0.99) * r[1] - r[2] + 1e-9);
  return Math.round((w * 13) / 3 + 1e-9);
}

/**
 * Reverse calculation: solves gross pay from net pay in cents.
 * Returns exact tax hits where tax(netC + T*100) == T, or nearest fallback.
 * @param netC Net pay in cents
 * @param S Scale table (S2 = with TFT, S1 = no TFT)
 */
export function solveNetToGross(netC: number, S: ScaleTable): SolveResult {
  const hits: number[] = [];
  let best: BestMatch | null = null;
  const max = Math.ceil((netC / 100) * 1.2) + 50;

  for (let T = 0; T <= max; T++) {
    const t = calculateTaxFromCents(netC + T * 100, S);
    if (t === T) {
      hits.push(T);
    }
    const d = Math.abs(t - T);
    if (!best || d <= best.d) {
      best = { T, d };
    }
  }

  return { hits, best: best! };
}
